import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLandlordStore, StaffRole, StaffInvitePayload } from '@/stores/useLandlordStore';
import { ArrowLeft, Loader2, Mail, Briefcase, Shield, DollarSign, Phone, User, AlertCircle, CheckCircle } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';

const AVAILABLE_PERMISSIONS = [
    { id: 'view_financials', label: 'View Financials' },
    { id: 'manage_leases', label: 'Manage Leases' },
    { id: 'manage_maintenance', label: 'Manage Maintenance' },
    { id: 'communicate_tenants', label: 'Communicate with Tenants' }
];

const AVAILABLE_ROLES: StaffRole[] = ['Property Manager', 'Maintenance Staff', 'Accountant', 'Security', 'Admin'];

interface FormErrors {
    staffEmail?: string;
    staffName?: string;
    staffPhone?: string;
    propertyId?: string;
    role?: string;
    compensationAmount?: string;
}

export const AddStaff: React.FC = () => {
    const navigate = useNavigate();
    const { addStaff, fetchStaff, properties, fetchProperties } = useLandlordStore();
    const { success, error: toastError } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    const [formData, setFormData] = useState({
        staffName: '',
        staffEmail: '',
        staffPhone: '',
        propertyId: '',
        role: 'Property Manager' as StaffRole,
        compensationAmount: '',
        compensationFrequency: 'Monthly',
        permissions: [] as string[]
    });

    useEffect(() => {
        if (properties.length === 0) {
            fetchProperties();
        } else if (!formData.propertyId) {
            setFormData(prev => ({ ...prev, propertyId: properties[0].id }));
        }
    }, [properties]);

    // Validation functions
    const validateEmail = (email: string): string | undefined => {
        if (!email) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) return 'Please enter a valid email address';
        return undefined;
    };

    const validateName = (name: string): string | undefined => {
        if (!name) return 'Name is required';
        if (name.length < 2) return 'Name must be at least 2 characters';
        if (name.length > 100) return 'Name must be less than 100 characters';
        return undefined;
    };

    const validatePhone = (phone: string): string | undefined => {
        if (!phone) return undefined; // Phone is optional
        const phoneRegex = /^[\d\s\-\+\(\)]{10,}$/;
        if (!phoneRegex.test(phone)) return 'Please enter a valid phone number';
        return undefined;
    };

    const validateProperty = (propertyId: string): string | undefined => {
        if (!propertyId) return 'Please select a property';
        return undefined;
    };

    const validateCompensation = (amount: string): string | undefined => {
        if (!amount) return 'Compensation amount is required';
        const num = parseFloat(amount);
        if (isNaN(num) || num <= 0) return 'Please enter a valid amount greater than 0';
        if (num > 10000000) return 'Amount seems too high';
        return undefined;
    };

    // Handle field blur for validation
    const handleBlur = (field: string) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    // Update form errors when fields change
    useEffect(() => {
        const errors: FormErrors = {};
        if (touched.staffEmail) errors.staffEmail = validateEmail(formData.staffEmail);
        if (touched.staffName) errors.staffName = validateName(formData.staffName);
        if (touched.staffPhone) errors.staffPhone = validatePhone(formData.staffPhone);
        if (touched.propertyId) errors.propertyId = validateProperty(formData.propertyId);
        if (touched.compensationAmount) errors.compensationAmount = validateCompensation(formData.compensationAmount);
        setFormErrors(errors);
    }, [formData, touched]);

    const handlePermissionToggle = (permissionId: string) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permissionId)
                ? prev.permissions.filter(p => p !== permissionId)
                : [...prev.permissions, permissionId]
        }));
    };

    const validateForm = (): boolean => {
        const errors: FormErrors = {};
        
        const emailError = validateEmail(formData.staffEmail);
        if (emailError) errors.staffEmail = emailError;
        
        const nameError = validateName(formData.staffName);
        if (nameError) errors.staffName = nameError;
        
        const phoneError = validatePhone(formData.staffPhone);
        if (phoneError) errors.staffPhone = phoneError;
        
        const propertyError = validateProperty(formData.propertyId);
        if (propertyError) errors.propertyId = propertyError;
        
        const compError = validateCompensation(formData.compensationAmount);
        if (compError) errors.compensationAmount = compError;

        setFormErrors(errors);
        setTouched({
            staffEmail: true,
            staffName: true,
            staffPhone: true,
            propertyId: true,
            compensationAmount: true
        });

        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) {
            setError('Please correct the errors below before submitting.');
            return;
        }

        setSubmitting(true);

        try {
            const payload: StaffInvitePayload = {
                staffEmail: formData.staffEmail,
                propertyId: formData.propertyId,
                role: formData.role,
                compensationDetails: {
                    amount: parseFloat(formData.compensationAmount),
                    currency: 'KES',
                    frequency: formData.compensationFrequency
                },
                permissions: formData.permissions
            };

            await addStaff(payload);
            await fetchStaff(formData.propertyId);
            success('Invitation Sent', `${formData.staffName} has been invited successfully.`);
            navigate('/landlord/staff');
        } catch (err: any) {
            setError(err.message || 'An error occurred while inviting the staff member.');
            toastError('Invitation Failed', err.message || 'Could not send invitation.');
        } finally {
            setSubmitting(false);
        }
    };

    const getInputClasses = (fieldName: string, hasError?: boolean) => {
        const baseClasses = "w-full pl-12 pr-4 py-3 rounded-xl border bg-light-input dark:bg-dark-input text-light-text dark:text-dark-text focus:ring-2 focus:ring-primary/50 outline-none font-medium transition-all";
        if (hasError) {
            return `${baseClasses} border-red-500 focus:ring-red-500/50`;
        }
        return `${baseClasses} border-light-border dark:border-dark-border`;
    };

    const renderFieldError = (error?: string) => {
        if (!error) return null;
        return (
            <div className="flex items-center gap-1 mt-1.5 text-red-600 dark:text-red-400 text-sm font-medium">
                <AlertCircle size={14} />
                {error}
            </div>
        );
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-24">
            {/* Back Button Header */}
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate('/landlord/staff')}
                    className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-full transition-colors"
                >
                    <ArrowLeft size={20} className="text-light-subtle dark:text-dark-subtle" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
                        <Briefcase className="text-primary" size={24} />
                        Invite New Staff
                    </h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle mt-0.5">
                        Fill in the details to send an invitation to a new team member.
                    </p>
                </div>
            </div>

            {/* Main Form */}
            <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                {error && (
                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl text-sm border border-red-200 dark:border-red-800 font-medium flex items-center gap-2">
                        <AlertCircle size={18} />
                        {error}
                    </div>
                )}

                <form id="addStaffForm" onSubmit={handleSubmit} className="space-y-8">
                    {/* Property Selection */}
                    <div className="max-w-xl">
                        <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                            Assign to Property <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Shield className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                            <select
                                required
                                value={formData.propertyId}
                                onChange={(e) => setFormData({ ...formData, propertyId: e.target.value })}
                                onBlur={() => handleBlur('propertyId')}
                                className={getInputClasses('propertyId', !!formErrors.propertyId).replace('pl-12', 'pl-12')}
                            >
                                <option value="" disabled>Select a property</option>
                                {properties.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                        {renderFieldError(formErrors.propertyId)}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Name */}
                        <div>
                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                Full Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                <input
                                    type="text"
                                    required
                                    placeholder="John Doe"
                                    value={formData.staffName}
                                    onChange={(e) => setFormData({ ...formData, staffName: e.target.value })}
                                    onBlur={() => handleBlur('staffName')}
                                    className={getInputClasses('staffName', !!formErrors.staffName)}
                                />
                            </div>
                            {renderFieldError(formErrors.staffName)}
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                Email Address <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                <input
                                    type="email"
                                    required
                                    placeholder="staff@example.com"
                                    value={formData.staffEmail}
                                    onChange={(e) => setFormData({ ...formData, staffEmail: e.target.value })}
                                    onBlur={() => handleBlur('staffEmail')}
                                    className={getInputClasses('staffEmail', !!formErrors.staffEmail)}
                                />
                            </div>
                            {renderFieldError(formErrors.staffEmail)}
                        </div>

                        {/* Phone */}
                        <div>
                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                Phone Number <span className="text-light-subtle dark:text-dark-subtle font-normal">(Optional)</span>
                            </label>
                            <div className="relative">
                                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                <input
                                    type="tel"
                                    placeholder="+254 700 123 456"
                                    value={formData.staffPhone}
                                    onChange={(e) => setFormData({ ...formData, staffPhone: e.target.value })}
                                    onBlur={() => handleBlur('staffPhone')}
                                    className={getInputClasses('staffPhone', !!formErrors.staffPhone)}
                                />
                            </div>
                            {renderFieldError(formErrors.staffPhone)}
                        </div>

                        {/* Role */}
                        <div>
                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                Role <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Shield className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                <select
                                    required
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value as StaffRole })}
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-light-border dark:border-dark-border bg-light-input dark:bg-dark-input text-light-text dark:text-dark-text focus:ring-2 focus:ring-primary/50 outline-none font-medium"
                                >
                                    {AVAILABLE_ROLES.map(role => (
                                        <option key={role} value={role}>{role}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Compensation */}
                        <div className="md:col-span-2 max-w-xl">
                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                Compensation (KES) <span className="text-red-500">*</span>
                            </label>
                            <div className="flex rounded-xl overflow-hidden border border-light-border dark:border-dark-border focus-within:ring-2 focus-within:ring-primary/50">
                                <div className="relative flex-1">
                                    <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                    <input
                                        type="number"
                                        required
                                        min="0"
                                        step="0.01"
                                        placeholder="Amount"
                                        value={formData.compensationAmount}
                                        onChange={(e) => setFormData({ ...formData, compensationAmount: e.target.value })}
                                        onBlur={() => handleBlur('compensationAmount')}
                                        className={`w-full pl-12 pr-4 py-3 bg-light-input dark:bg-dark-input text-light-text dark:text-dark-text outline-none font-medium border-r border-light-border dark:border-dark-border ${formErrors.compensationAmount ? 'border-red-500 focus:ring-red-500/50' : ''}`}
                                    />
                                </div>
                                <select
                                    value={formData.compensationFrequency}
                                    onChange={(e) => setFormData({ ...formData, compensationFrequency: e.target.value })}
                                    className="px-4 py-3 bg-light-input dark:bg-dark-input text-light-text dark:text-dark-text outline-none w-40 font-bold"
                                >
                                    <option value="Daily">Daily</option>
                                    <option value="Weekly">Weekly</option>
                                    <option value="Monthly">Monthly</option>
                                </select>
                            </div>
                            {renderFieldError(formErrors.compensationAmount)}
                        </div>
                    </div>

                    {/* Permissions */}
                    <div>
                        <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-4 flex items-center gap-2">
                            <Shield size={20} className="text-light-subtle dark:text-dark-subtle" />
                            Initial Access Permissions
                            <span className="text-light-subtle dark:text-dark-subtle font-normal text-xs ml-2">(Select all that apply)</span>
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {AVAILABLE_PERMISSIONS.map(permission => (
                                <label
                                    key={permission.id}
                                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                        formData.permissions.includes(permission.id)
                                            ? 'border-primary bg-primary/10 shadow-sm'
                                            : 'border-light-border dark:border-dark-border hover:border-primary/50 bg-light-input dark:bg-dark-input'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={formData.permissions.includes(permission.id)}
                                        onChange={() => handlePermissionToggle(permission.id)}
                                        className="mt-1 w-4 h-4 rounded text-primary focus:ring-primary flex-shrink-0"
                                    />
                                    <span className={`text-sm font-bold ${
                                        formData.permissions.includes(permission.id)
                                            ? 'text-primary'
                                            : 'text-light-subtle dark:text-dark-subtle'
                                    }`}>
                                        {permission.label}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Summary Preview */}
                    {formData.staffName && formData.staffEmail && formData.role && (
                        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                            <h3 className="text-sm font-bold text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                                <CheckCircle size={16} />
                                Invitation Preview
                            </h3>
                            <p className="text-sm text-blue-800 dark:text-blue-200">
                                An invitation will be sent to <strong>{formData.staffEmail}</strong> to join as <strong>{formData.role}</strong> 
                                {formData.staffPhone && ` (Phone: ${formData.staffPhone})`}.
                            </p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-4 pt-6 mt-8 border-t border-light-border dark:border-dark-border">
                        <button
                            type="button"
                            onClick={() => navigate('/landlord/staff')}
                            disabled={submitting}
                            className="px-8 py-3 rounded-xl font-bold text-light-subtle dark:text-dark-subtle hover:bg-light-input dark:hover:bg-dark-input border border-light-border dark:border-dark-border transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex items-center justify-center gap-2 bg-primary text-white px-8 py-3 rounded-xl font-bold hover:bg-primary/90 transition-all shadow-md disabled:opacity-50 disabled:shadow-none min-w-[200px]"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={20} className="animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                'Send Invitation'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};